import { Link } from "react-router-dom";
import {
  ArrowRight, Percent, Gift, Tag, Package, Clock, Crown,
  MessageCircle, Mail, Send, Instagram, Facebook, Globe, Brain, Sparkles,
} from "lucide-react";
import Reveal from "@/marketing/Reveal";
import { useI18n } from "@/i18n/I18nContext";

const CHANNELS = [
  { icon: MessageCircle, t: "WhatsApp" }, { icon: Mail, t: "Email" }, { icon: Send, t: "SMS" },
  { icon: Instagram, t: "Instagram" }, { icon: Facebook, t: "Facebook" }, { icon: Globe, t: "Website" },
];

export default function PromotionsMarketing() {
  const { lang } = useI18n();
  const es = lang === "es";

  const PROMO_TYPES = [
    { icon: Percent, t: es ? "Descuentos por porcentaje" : "Percentage discounts", d: es ? "20% de descuento en color este mes." : "20% off color services this month." },
    { icon: Tag, t: es ? "Descuentos fijos" : "Fixed discounts", d: es ? "₡5,000 de descuento en tratamientos premium." : "₡5,000 off any premium treatment." },
    { icon: Gift, t: es ? "Lleva uno, gratis otro" : "Buy One Get One", d: es ? "Reserva un corte y obtén un brushing gratis." : "Book a cut, get a blowout free." },
    { icon: Package, t: es ? "Paquetes y combos" : "Package & bundle pricing", d: es ? "Paquetes de novia y días de spa." : "Bridal packages and spa days." },
    { icon: Clock, t: es ? "Tiempo limitado y happy hour" : "Limited-time & happy hour", d: es ? "Llena las horas tranquilas automáticamente." : "Fill quiet hours automatically." },
    { icon: Crown, t: es ? "Ofertas VIP y cumpleaños" : "VIP & birthday offers", d: es ? "Premia a tus clientes más fieles." : "Reward your most loyal clients." },
  ];
  const CATEGORIES = es
    ? ["Cortes", "Coloración", "Mechas", "Keratina", "Barba", "Afeitado", "Spa", "Masajes", "Faciales", "Depilación", "Láser", "Uñas", "Uñas en Gel", "Pedicura", "Pestañas", "Cejas", "Maquillaje", "Novias", "Membresías", "Festivos", "Cumpleaños", "Referidos", "Gift Cards", "Temporada"]
    : ["Haircuts", "Hair Coloring", "Highlights", "Keratin", "Beard", "Shaving", "Spa", "Massages", "Facials", "Waxing", "Laser", "Nails", "Gel Nails", "Pedicures", "Lashes", "Brows", "Makeup", "Bridal", "Memberships", "Holiday", "Birthday", "Referral", "Gift Cards", "Seasonal"];
  const AI_INPUTS = es
    ? ["Historial del cliente", "Temporada", "Tendencia de ingresos", "Inventario", "Disponibilidad del personal", "Rendimiento del negocio"]
    : ["Customer history", "Seasonality", "Revenue trends", "Inventory levels", "Staff availability", "Business performance"];
  const SUGG = es
    ? [{ t: "Color Entre Semana", d: "20% en color · Mar–Jue", tag: "Llena días tranquilos" }, { t: "Trae a un Amigo", d: "2x1 en brushing este fin", tag: "Más referidos" }, { t: "Brillo de Cumpleaños", d: "₡8,000 en faciales tu mes", tag: "Fideliza" }]
    : [{ t: "Mid-week Color Refresh", d: "20% off color · Tue–Thu", tag: "Fills quiet days" }, { t: "Bring a Friend", d: "BOGO blowouts this weekend", tag: "Boosts referrals" }, { t: "Birthday Glow", d: "₡8,000 off facials in your birth month", tag: "Drives loyalty" }];

  return (
    <div>
      <section className="pt-36 sm:pt-44 pb-16 px-5 sm:px-8 relative overflow-hidden">
        <div className="absolute top-[-10%] left-[-5%] h-[450px] w-[450px] rounded-full bg-[#B86B5A]/10 blur-[120px] -z-10" />
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#B86B5A] mb-4">{es ? "Promociones" : "Promotions"}</p>
            <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tighter text-[#0A0A0A] leading-[0.95]">
              {es ? "Lanza campañas que " : "Launch campaigns that "}<span className="font-serif-lux italic font-medium text-[#B86B5A]">{es ? "de verdad venden." : "actually sell."}</span>
            </h1>
            <p className="mt-6 text-lg text-[#2C3E35]/75 leading-relaxed">
              {es ? "Crea promociones hermosas en segundos — sin conocimientos técnicos. La IA de BellaOS recomienda la oferta ideal y la comparte donde están tus clientes." : "Create beautiful promotions in seconds — no technical knowledge required. BellaOS AI recommends the right offer, then shares it everywhere your clients are."}
            </p>
            <Link to="/register" data-testid="promo-hero-cta"
              className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#2C3E35] text-[#FDFBF9] font-semibold hover:bg-[#0A0A0A] transition-all duration-300 hover:shadow-[0_0_40px_rgba(212,175,55,0.3)]">
              {es ? "Crea tu primera promoción" : "Create your first promotion"} <ArrowRight size={18} />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl bg-[#2C3E35] text-[#FDFBF9] p-8 shadow-[0_20px_60px_rgba(44,62,53,0.18)] relative overflow-hidden">
              <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#D4AF37]/15 blur-2xl" />
              <div className="flex items-center gap-2 text-[#D4AF37] mb-5"><Sparkles size={18} /> <span className="text-sm font-semibold uppercase tracking-wider">{es ? "Sugerido por IA" : "AI Suggested"}</span></div>
              <div className="space-y-3">
                {SUGG.map((p) => (
                  <div key={p.t} className="rounded-xl bg-[#FDFBF9]/8 border border-[#FDFBF9]/10 p-4">
                    <div className="flex items-center justify-between">
                      <div className="font-semibold">{p.t}</div>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D4AF37] text-[#2C3E35] font-bold uppercase tracking-wide">{p.tag}</span>
                    </div>
                    <div className="text-sm text-[#FDFBF9]/65 mt-1">{p.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-20">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-12">
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A]">{es ? "Todo tipo de oferta" : "Every kind of offer"}</h2>
            <p className="mt-4 text-lg text-[#2C3E35]/75">{es ? "Desde descuentos simples hasta combos sofisticados y beneficios VIP." : "From simple discounts to sophisticated bundles and VIP perks."}</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PROMO_TYPES.map((p, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="h-full rounded-2xl bg-white border border-[#2C3E35]/10 p-7 shadow-[0_8px_32px_rgba(44,62,53,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_48px_rgba(44,62,53,0.12)]">
                  <div className="h-12 w-12 rounded-xl bg-[#B86B5A]/10 flex items-center justify-center mb-4">
                    <p.icon size={22} strokeWidth={1.5} className="text-[#B86B5A]" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-[#0A0A0A]">{p.t}</h3>
                  <p className="mt-1.5 text-sm text-[#2C3E35]/70">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-20 bg-[#F5F2ED]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#B86B5A] mb-4">{es ? "Recomendaciones IA" : "AI Recommendations"}</p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A]">
              {es ? "La promo correcta, " : "The right promo, "}<span className="font-serif-lux italic font-medium">{es ? "en el momento justo." : "at the right time."}</span>
            </h2>
            <p className="mt-5 text-lg text-[#2C3E35]/75 leading-relaxed">{es ? "BellaOS analiza tu negocio y sugiere promociones diseñadas para convertir — y escribe el texto por ti." : "BellaOS analyzes your business and suggests promotions designed to convert — then writes the copy for you."}</p>
            <div className="mt-7 grid grid-cols-2 gap-3">
              {AI_INPUTS.map((tt) => (
                <div key={tt} className="flex items-center gap-2 rounded-xl bg-white border border-[#2C3E35]/10 px-4 py-3 text-sm font-medium text-[#2C3E35]">
                  <Brain size={16} className="text-[#D4AF37]" /> {tt}
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div>
              <h3 className="font-display text-xl font-bold text-[#0A0A0A] mb-5">{es ? "Comparte en todas partes, al instante" : "Share everywhere, instantly"}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {CHANNELS.map((c) => (
                  <div key={c.t} className="rounded-2xl bg-white border border-[#2C3E35]/10 p-5 text-center shadow-[0_8px_32px_rgba(44,62,53,0.05)] transition-transform duration-300 hover:-translate-y-1">
                    <c.icon size={24} strokeWidth={1.5} className="text-[#2C3E35] mx-auto" />
                    <div className="text-sm font-medium text-[#0A0A0A] mt-2">{c.t}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 py-20">
        <div className="max-w-5xl mx-auto text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-3">{es ? "Para cada servicio que ofreces" : "For every service you offer"}</h2>
            <p className="text-[#2C3E35]/70 mb-9">{es ? "Crea promociones para cualquier tratamiento, paquete u ocasión." : "Build promotions for any treatment, package or occasion."}</p>
            <div className="flex flex-wrap justify-center gap-2.5">
              {CATEGORIES.map((c) => (
                <span key={c} className="px-4 py-2 rounded-full bg-white border border-[#2C3E35]/10 text-sm font-medium text-[#2C3E35] hover:border-[#D4AF37]/50 hover:text-[#B86B5A] transition-colors">{c}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="max-w-5xl mx-auto rounded-[2rem] bg-[#2C3E35] text-[#FDFBF9] p-12 sm:p-16 text-center relative overflow-hidden">
            <div className="absolute -bottom-20 -right-20 h-72 w-72 rounded-full bg-[#B86B5A]/15 blur-[100px]" />
            <div className="relative">
              <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight">{es ? "Convierte los días tranquilos en los más ocupados." : "Turn quiet days into your busiest."}</h2>
              <Link to="/register" data-testid="promo-footer-cta"
                className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#D4AF37] text-[#2C3E35] font-bold hover:bg-[#e0bd4a] transition-colors">
                {es ? "Prueba gratis" : "Start free trial"} <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
